import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Menu, X, Home, Info, Calendar, Folder, History, UserPlus, Users } from 'lucide-react';
import './Header.css';

const Header = ({ onPageChange, currentPage }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeNavItem, setActiveNavItem] = useState('home');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Sync activeNavItem with currentPage prop
    setActiveNavItem(currentPage);
  }, [currentPage]);

  const [isHidden, setIsHidden] = useState(false);

  useEffect(() => {
    const handleToggle = (e) => setIsHidden(e.detail.hidden);
    window.addEventListener('toggleHeaderVisibility', handleToggle);
    return () => window.removeEventListener('toggleHeaderVisibility', handleToggle);
  }, []);

  // Existing navItems and handle logic... (truncated for brevity in replacement, but ensuring logic remains)
  const navItems = [
    { name: 'Home', page: 'home', icon: Home },
    { name: 'About', href: '#about', icon: Info },
    { name: 'Events', href: '#events', icon: Calendar, route: '/events' },
    { name: 'Projects', href: '#projects', icon: Folder },
    { name: 'Team', page: 'team', icon: Users },
    { name: 'Past Events', href: '#past-events', icon: History }
  ];

  // ... (keeping existing handler functions)

  const handleAnchorNav = (href) => {
    const id = href.replace('#', '');
    if (window.location.pathname !== '/') {
      window.history.pushState({}, '', '/');
      const delay = window.innerWidth <= 768 ? 100 : 50;
      setTimeout(() => { scrollToSection(id); }, delay);
    } else {
      scrollToSection(id);
    }
    const event = new CustomEvent('navigateToSection', { detail: { id } });
    window.dispatchEvent(event);
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const headerHeight = document.querySelector('.header')?.offsetHeight || 80;
      const elementTop = element.offsetTop - headerHeight - 20;
      window.scrollTo({ top: elementTop, behavior: 'smooth' });
    }
  };

  const handleNavClick = (item) => {
    if (item.page) {
      setActiveNavItem(item.page);
    } else if (item.href) {
      const id = item.href.replace('#', '');
      setActiveNavItem(id);
    }

    if (item.href) {
      if (item.route) {
        window.history.pushState({}, '', item.route);
        const id = item.href.replace('#', '');
        const event = new CustomEvent('navigateToSection', { detail: { id } });
        window.dispatchEvent(event);
        scrollToSection(id);
      } else {
        handleAnchorNav(item.href);
      }
    } else if (item.page) {
      if (item.page === 'home') {
        onPageChange('home');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        onPageChange(item.page);
      }
    }
  };

  return (
    <motion.header
      className={`header ${isScrolled ? 'scrolled' : ''}`}
      initial={{ y: -100 }}
      animate={{ y: isHidden ? -100 : 0 }}
      transition={{ duration: 0.6 }}
    >
      <div className="container">
        <div className="header-content">
          {/* Logo */}
          <motion.div
            className="logo"
            whileHover={{ scale: 1.05 }}
            transition={{ type: "spring", stiffness: 400, damping: 10 }}
            onClick={() => {
              onPageChange('home');
              setActiveNavItem('home');

              // Simple smooth scroll to top
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            style={{ cursor: 'pointer' }}
          >
            <img
              src="/logo.png"
              alt="AIEC IITM - Premier Technical Society Logo"
              className="logo-image"
              loading="eager"
            />
            <span className="logo-text">AIEC IITM</span>
          </motion.div>

          {/* Desktop Navigation */}
          <nav className="nav-desktop" role="navigation" aria-label="Main navigation">
            <ul className="nav-list" role="menubar">
              {navItems.map((item, index) => (
                <motion.li
                  key={item.name}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -2 }}
                  role="none"
                >
                  <button
                    onClick={() => handleNavClick(item)}
                    className={`nav-link ${activeNavItem === (item.page || item.href?.replace('#', '')) ? 'active' : ''}`}
                    role="menuitem"
                    aria-label={`Navigate to ${item.name} section`}
                  >
                    {item.icon && <item.icon className="nav-icon" size={18} aria-hidden="true" />}
                    {item.name}
                  </button>
                </motion.li>
              ))}
            </ul>
          </nav>

          {/* Desktop CTA */}
          <motion.button
            className="cta-button header-cta"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => onPageChange('careers')}
          >
            <UserPlus size={18} style={{ marginRight: '0.5rem' }} />
            Join Us
          </motion.button>

          {/* Mobile Menu Button */}
          <motion.button
            className="mobile-menu-button"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            whileTap={{ scale: 0.95 }}
          >
            {isMobileMenuOpen ? <X /> : <Menu />}
          </motion.button>
        </div>

        {/* Mobile Navigation */}
        <motion.nav
          className={`nav-mobile ${isMobileMenuOpen ? 'open' : ''}`}
          initial={{ opacity: 0, height: 0 }}
          animate={{
            opacity: isMobileMenuOpen ? 1 : 0,
            height: isMobileMenuOpen ? 'auto' : 0
          }}
          transition={{ duration: 0.3 }}
        >
          <ul className="nav-mobile-list">
            {navItems.map((item, index) => (
              <motion.li
                key={item.name}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <button
                  onClick={() => {
                    handleNavClick(item);
                    setIsMobileMenuOpen(false);
                  }}
                  className={`nav-mobile-link ${activeNavItem === (item.page || item.href?.replace('#', '')) ? 'active' : ''}`}
                >
                  {item.icon && <item.icon className="nav-icon" size={18} />} {item.name}
                </button>
              </motion.li>
            ))}
            {/* Join Us item (mobile only) */}
            <motion.li
              key="Join Us"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: navItems.length * 0.1 }}
            >
              <button
                className="nav-mobile-link"
                onClick={() => {
                  onPageChange('careers');
                  setIsMobileMenuOpen(false);
                }}
              >
                <UserPlus className="nav-icon" size={18} /> Join Us
              </button>
            </motion.li>
          </ul>
        </motion.nav>
      </div>
    </motion.header>
  );
};

export default Header;
